import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Home() {
  const navigate = useNavigate();
  
  // Interactive neuron network mouse tracking
  React.useEffect(() => {
    const canvas = document.getElementById('neuron-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Neuron nodes
    const nodes: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      targetX: number;
      targetY: number;
      baseSpeed: number;
      glowIntensity: number;
      glowPhase: number;
      connections: number[];
    }> = [];
    const maxNodes = 50;
    const maxDistance = 150;
    let mouse = { x: canvas.width / 2, y: canvas.height / 2 };

    // Initialize nodes with constant movement
    for (let i = 0; i < maxNodes; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      nodes.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 1.5, // Increased base velocity
        vy: (Math.random() - 0.5) * 1.5,
        targetX: x,
        targetY: y,
        baseSpeed: 0.3 + Math.random() * 0.4, // Random base speed for each node
        glowIntensity: Math.random(),
        glowPhase: Math.random() * Math.PI * 2, // Random phase for glow animation
        connections: []
      });
    }

    // Mouse move handler
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    let animationTime = 0;

    const animate = () => {
      animationTime += 0.016; // Approximately 60fps
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw nodes
      nodes.forEach((node, i) => {
        // Constant autonomous movement
        node.targetX += Math.sin(animationTime * node.baseSpeed + i) * 0.5;
        node.targetY += Math.cos(animationTime * node.baseSpeed + i * 0.7) * 0.5;

        // Mouse attraction (stronger effect)
        const dx = mouse.x - node.x;
        const dy = mouse.y - node.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 300) {
          const force = (300 - distance) / 300;
          node.vx += (dx / distance) * force * 0.05;
          node.vy += (dy / distance) * force * 0.05;
        }

        // Add some drift towards target position
        node.vx += (node.targetX - node.x) * 0.001;
        node.vy += (node.targetY - node.y) * 0.001;

        // Update position with constant movement
        node.x += node.vx;
        node.y += node.vy;

        // Boundary handling with smoother bounce
        if (node.x < 0) {
          node.x = 0;
          node.vx = Math.abs(node.vx) * 0.7;
          node.targetX = 100 + Math.random() * 200;
        }
        if (node.x > canvas.width) {
          node.x = canvas.width;
          node.vx = -Math.abs(node.vx) * 0.7;
          node.targetX = canvas.width - 100 - Math.random() * 200;
        }
        if (node.y < 0) {
          node.y = 0;
          node.vy = Math.abs(node.vy) * 0.7;
          node.targetY = 100 + Math.random() * 200;
        }
        if (node.y > canvas.height) {
          node.y = canvas.height;
          node.vy = -Math.abs(node.vy) * 0.7;
          node.targetY = canvas.height - 100 - Math.random() * 200;
        }

        // Apply gentle friction but keep movement
        node.vx *= 0.995;
        node.vy *= 0.995;

        // Ensure minimum movement
        const speed = Math.sqrt(node.vx * node.vx + node.vy * node.vy);
        if (speed < 0.1) {
          const angle = Math.random() * Math.PI * 2;
          node.vx += Math.cos(angle) * 0.2;
          node.vy += Math.sin(angle) * 0.2;
        }

        // Update glow animation
        node.glowPhase += 0.05 + node.baseSpeed * 0.1;
        node.glowIntensity = 0.5 + Math.sin(node.glowPhase) * 0.5;

        // Draw connections
        node.connections = [];
        for (let j = i + 1; j < nodes.length; j++) {
          const other = nodes[j];
          const dist = Math.sqrt((node.x - other.x) ** 2 + (node.y - other.y) ** 2);
          
          if (dist < maxDistance) {
            node.connections.push(j);
            
            // Connection opacity based on distance and mouse proximity
            const baseOpacity = (1 - dist / maxDistance) * 0.4;
            const mouseDist = Math.sqrt((mouse.x - (node.x + other.x) / 2) ** 2 + (mouse.y - (node.y + other.y) / 2) ** 2);
            const mouseInfluence = mouseDist < 200 ? (200 - mouseDist) / 200 : 0;
            
            // Animated connection glow
            const connectionGlow = Math.sin(animationTime * 2 + dist * 0.01) * 0.2 + 0.8;
            const finalOpacity = (baseOpacity + mouseInfluence * 0.6) * connectionGlow;
            
            ctx.strokeStyle = `rgba(34, 211, 238, ${Math.min(finalOpacity, 1)})`;
            ctx.lineWidth = 1 + mouseInfluence * 2 + connectionGlow * 0.5;
            
            // Add glow effect to connections
            ctx.shadowColor = 'rgba(34, 211, 238, 0.8)';
            ctx.shadowBlur = 3 + mouseInfluence * 8;
            
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
            
            ctx.shadowBlur = 0;
          }
        }

        // Draw node with enhanced glow
        const nodeMouseDist = Math.sqrt((mouse.x - node.x) ** 2 + (mouse.y - node.y) ** 2);
        const nodeInfluence = nodeMouseDist < 150 ? (150 - nodeMouseDist) / 150 : 0;
        const baseSize = 2 + Math.sin(animationTime * 3 + i) * 0.5; // Pulsing base size
        const nodeSize = baseSize + nodeInfluence * 3 + node.glowIntensity * 1.5;
        const nodeOpacity = 0.7 + nodeInfluence * 0.3 + node.glowIntensity * 0.3;

        // Draw multiple glow layers for better effect
        ctx.shadowColor = `rgba(34, 211, 238, ${node.glowIntensity * 0.8})`;
        ctx.shadowBlur = 15 + node.glowIntensity * 10;
        
        ctx.fillStyle = `rgba(34, 211, 238, ${Math.min(nodeOpacity, 1)})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize, 0, Math.PI * 2);
        ctx.fill();

        // Inner bright core
        ctx.shadowBlur = 5;
        ctx.fillStyle = `rgba(255, 255, 255, ${node.glowIntensity * 0.6 + nodeInfluence * 0.4})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize * 0.4, 0, Math.PI * 2);
        ctx.fill();

        // Reset shadow
        ctx.shadowBlur = 0;
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Mortgage Refinance',
      description:
        'Specialized lead generation for mortgage refinancing companies targeting homeowners looking to reduce monthly payments and access equity.',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: 'ri-file-check-line',
      title: 'Pre-underwriting AI',
      description:
        'Advanced AI-powered pre-qualification services for lenders, streamlining the loan approval process with qualified prospects ready for underwriting.',
      color: 'from-purple-500 to-purple-600',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Debt Settlement',
      description:
        'Generate qualified leads for debt consolidation services, targeting individuals seeking financial relief and better payment terms.',
      color: 'from-pink-500 to-pink-600',
    },
    {
      icon: 'ri-phone-line',
      title: 'Inbound Calls',
      description:
        'Optimize inbound call management and qualification processes to maximize conversion rates from all marketing channels.',
      color: 'from-green-500 to-green-600',
    },
    {
      icon: 'ri-health-book-line',
      title: 'Health Insurance',
      description:
        'Connect health insurance providers with qualified prospects seeking comprehensive coverage and competitive rates.',
      color: 'from-orange-500 to-orange-600',
    },
    {
      icon: 'ri-shield-keyhole-line',
      title: 'Home Security',
      description:
        'Generate leads for home security companies targeting homeowners interested in protecting their property and family.',
      color: 'from-red-500 to-red-600',
    },
  ];

  const industries = [
    {
      title: 'Mortgage Refinance',
      description:
        'Specialized lead generation for mortgage refinancing companies targeting homeowners looking to reduce monthly payments and access equity.',
      image:
        'https://readdy.ai/api/search-image?query=Professional%20mortgage%20consultant%20meeting%20with%20happy%20family%20in%20modern%20bright%20office%2C%20reviewing%20home%20loan%20documents%20with%20calculator%20and%20house%20model%20on%20polished%20desk%2C%20warm%20welcoming%20atmosphere%20with%20natural%20lighting&width=500&height=350&seq=mortgage2&orientation=landscape',
      stats: { leads: '50K+', conversion: '15%' },
    },
    {
      title: 'Debt Settlement',
      description:
        'Generate qualified leads for debt consolidation services, targeting individuals seeking financial relief and better payment terms.',
      image:
        'https://readdy.ai/api/search-image?query=Compassionate%20financial%20advisor%20helping%20stressed%20couple%20with%20debt%20consolidation%20paperwork%20in%20comfortable%20consultation%20room%2C%20supportive%20atmosphere%20with%20charts%20showing%20debt%20reduction%2C%20professional%20yet%20caring%20environment&width=500&height=350&seq=debt2&orientation=landscape',
      stats: { leads: '30K+', conversion: '18%' },
    },
    {
      title: 'Home Security',
      description:
        'Connect home security companies with homeowners interested in protecting their property and family with advanced security systems.',
      image:
        'https://readdy.ai/api/search-image?query=Professional%20security%20specialist%20demonstrating%20smart%20home%20security%20system%20to%20interested%20homeowner%20in%20modern%20living%20room%2C%20showcasing%20control%20panel%20and%20mobile%20app%2C%20clean%20contemporary%20home%20interior&width=500&height=350&seq=security2&orientation=landscape',
      stats: { leads: '25K+', conversion: '22%' },
    },
  ];

  const stats = [
    {
      number: '2.5M+',
      label: 'Qualified Leads Generated',
      icon: 'ri-user-add-line',
    },
    { number: '180+', label: 'International Clients', icon: 'ri-global-line' },
    { number: '97%', label: 'Client Retention Rate', icon: 'ri-heart-line' },
    {
      number: '400%',
      label: 'Average ROI Increase',
      icon: 'ri-line-chart-line',
    },
  ];

  const testimonials = [
    {
      name: 'Marcus Thompson',
      company: 'Premier Home Security',
      role: 'CEO',
      quote:
        'Halcom Analytics revolutionized our lead generation. We saw a 400% increase in qualified home security leads within 90 days.',
      rating: 5,
      image:
        'https://readdy.ai/api/search-image?query=Professional%20African%20American%20CEO%20in%20navy%20business%20suit%20smiling%20confidently%20in%20modern%20executive%20office%20with%20security%20company%20branding%2C%20successful%20businessman%20portrait&width=100&height=100&seq=testimonial1&orientation=squarish',
    },
    {
      name: 'Sarah Chen',
      company: 'Liberty Mortgage Solutions',
      role: 'VP of Marketing',
      quote:
        'Their AI-powered pre-underwriting leads have a 95% qualification rate. Our loan officers are closing more deals than ever before.',
      rating: 5,
      image:
        'https://readdy.ai/api/search-image?query=Professional%20Asian%20female%20VP%20in%20elegant%20business%20attire%20smiling%20warmly%20in%20contemporary%20mortgage%20office%20setting%2C%20confident%20executive%20portrait%20with%20financial%20charts%20background&width=100&height=100&seq=testimonial2&orientation=squarish',
    },
    {
      name: 'Robert Martinez',
      company: 'Debt Relief Partners',
      role: 'Director of Operations',
      quote:
        'The quality of debt settlement leads from Halcom is exceptional. Our conversion rates improved by 250% in just 6 months.',
      rating: 5,
      image:
        'https://readdy.ai/api/search-image?query=Professional%20Hispanic%20male%20director%20in%20modern%20business%20attire%20smiling%20confidently%20in%20sleek%20corporate%20office%2C%20executive%20headshot%20with%20professional%20lighting%20and%20debt%20relief%20company%20setting&width=100&height=100&seq=testimonial3&orientation=squarish',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
        {/* Interactive Neuron Canvas */}
        <canvas
          id="neuron-canvas"
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 1 }}
        />

        {/* Gen AI Video Background */}
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover object-center opacity-40"
          >
            <source
              src="https://readdy.ai/api/search-image?query=Seamless%20looping%20generative%20AI%20visualization%20with%20dynamic%20neural%20networks%2C%20flowing%20digital%20particles%2C%20morphing%20geometric%20patterns%2C%20holographic%20data%20streams%20in%20cyan%20and%20purple%20colors%2C%20futuristic%20artificial%20intelligence%20interface%20elements%2C%20smooth%20continuous%20animation%20of%20interconnected%20nodes%20and%20glowing%20circuits%20on%20dark%20gradient%20background&width=1920&height=1080&seq=gen-ai-hero-video&orientation=landscape"
              type="video/mp4"
            />
            {/* Fallback image */}
            <img
              src="https://readdy.ai/api/search-image?query=Generative%20AI%20neural%20network%20visualization%20with%20glowing%20nodes%20and%20connections%2C%20digital%20brain%20patterns%2C%20geometric%20data%20flow%20lines%2C%20holographic%20interface%20elements%2C%20cyan%20and%20purple%20lighting%20effects%20on%20dark%20gradient%20background%2C%20modern%20artificial%20intelligence%20graphics%20with%20smooth%20flowing%20particles&width=1920&height=1080&seq=gen-ai-hero-bg&orientation=landscape"
              alt="Gen AI Background"
              className="w-full h-full object-cover object-center opacity-30"
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-blue-900/70 to-gray-900/80"></div>
        </div>

        {/* Background pattern overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%224%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

        {/* Animated geometric elements */}
        <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 2 }}>
          {/* Neuron nodes */}
          <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-cyan-400/40 rounded-full animate-pulse">
            <div className="absolute inset-0 bg-cyan-400/20 rounded-full animate-ping"></div>
          </div>
          <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-purple-400/40 rounded-full animate-pulse delay-500">
            <div className="absolute inset-0 bg-purple-400/20 rounded-full animate-ping delay-500"></div>
          </div>
          <div className="absolute bottom-1/3 left-1/3 w-4 h-4 bg-emerald-400/40 rounded-full animate-pulse delay-1000">
            <div className="absolute inset-0 bg-emerald-400/20 rounded-full animate-ping delay-1000"></div>
          </div>
          <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-blue-400/40 rounded-full animate-pulse delay-700">
            <div className="absolute inset-0 bg-blue-400/20 rounded-full animate-ping delay-700"></div>
          </div>
          <div className="absolute top-3/4 left-1/2 w-3 h-3 bg-indigo-400/40 rounded-full animate-pulse delay-300">
            <div className="absolute inset-0 bg-indigo-400/20 rounded-full animate-ping delay-300"></div>
          </div>
          <div className="absolute top-1/6 right-1/2 w-2 h-2 bg-teal-400/40 rounded-full animate-pulse delay-900">
            <div className="absolute inset-0 bg-teal-400/20 rounded-full animate-ping delay-900"></div>
          </div>

          {/* Connecting lines with animation */}
          <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
            <defs>
              <linearGradient id="lineGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(34, 211, 238)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="rgb(168, 85, 247)" stopOpacity="0.1" />
              </linearGradient>
              <linearGradient id="lineGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(16, 185, 129)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0.1" />
              </linearGradient>
            </defs>

            {/* Animated connecting lines */}
            <line x1="25%" y1="25%" x2="66%" y2="33%" stroke="url(#lineGradient1)" strokeWidth="1" opacity="0.6">
              <animate attributeName="opacity" values="0.3;0.8;0.3" dur="3s" repeatCount="indefinite" />
            </line>
            <line x1="33%" y1="66%" x2="75%" y2="50%" stroke="url(#lineGradient2)" strokeWidth="1" opacity="0.4">
              <animate attributeName="opacity" values="0.2;0.7;0.2" dur="4s" repeatCount="indefinite" begin="1s" />
            </line>
            <line x1="25%" y1="25%" x2="33%" y2="66%" stroke="url(#lineGradient1)" strokeWidth="1" opacity="0.5">
              <animate attributeName="opacity" values="0.3;0.8;0.3" dur="3.5s" repeatCount="indefinite" begin="0.5s" />
            </line>
            <line x1="66%" y1="33%" x2="50%" y2="75%" stroke="url(#lineGradient2)" strokeWidth="1" opacity="0.4">
              <animate attributeName="opacity" values="0.2;0.6;0.2" dur="4.5s" repeatCount="indefinite" begin="2s" />
            </line>
            <line x1="50%" y1="16%" x2="50%" y2="75%" stroke="url(#lineGradient1)" strokeWidth="1" opacity="0.3">
              <animate attributeName="opacity" values="0.1;0.5;0.1" dur="5s" repeatCount="indefinite" begin="1.5s" />
            </line>
            <line x1="16%" y1="50%" x2="75%" y2="50%" stroke="url(#lineGradient2)" strokeWidth="1" opacity="0.4">
              <animate attributeName="opacity" values="0.2;0.7;0.2" dur="3.8s" repeatCount="indefinite" begin="0.8s" />
            </line>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 w-full relative z-10">
          <div className="max-w-4xl">
            <div className="mb-4 md:mb-6">
              <span className="inline-block bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 backdrop-blur-sm text-cyan-300 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium border border-cyan-400/30">
                🚀 #1 International Lead Generation Agency
              </span>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-5 md:mb-8 leading-tight">
              Generate{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                Premium Leads
              </span>
              <br />
              for Global Markets
            </h1>
            <p className="text-base md:text-xl text-blue-100 mb-8 md:mb-10 leading-relaxed max-w-3xl">
              Specialized in SEO, SEM, Meta & Google Ads for mortgage refinance,
              debt consolidation, home security, and AI-powered solutions.
              Transform your business with data-driven strategies that deliver
              results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="text-base md:text-xl px-6 md:px-10 py-3 md:py-5 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 transform hover:scale-105 transition-all shadow-xl border-0"
              >
                <i className="ri-rocket-line mr-2 md:mr-3"></i>
                Start Generating Leads Now
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/case-studies')}
                className="text-base md:text-xl px-6 md:px-10 py-3 md:py-5 bg-white/10 backdrop-blur-sm border-2 border-cyan-300 text-cyan-300 hover:bg-cyan-300 hover:text-gray-900 transform hover:scale-105 transition-all"
              >
                <i className="ri-play-circle-line mr-2 md:mr-3"></i>
                View Success Stories
              </Button>
            </div>
            <div className="mt-8 md:mt-12 flex items-center space-x-5 md:space-x-8 flex-wrap gap-y-4">
              <div className="text-center">
                <div className="text-xl md:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">$500M+</div>
                <div className="text-blue-200 text-xs md:text-sm">Revenue Generated</div>
              </div>
              <div className="text-center">
                <div className="text-xl md:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">2.5M+</div>
                <div className="text-blue-200 text-xs md:text-sm">Leads Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-xl md:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">180+</div>
                <div className="text-blue-200 text-xs md:text-sm">Global Clients</div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional floating neuron elements */}
        <div className="absolute top-1/4 right-10 w-3 h-3" style={{ zIndex: 3 }}>
          <div className="w-full h-full bg-cyan-400/30 rounded-full animate-pulse">
            <div className="w-1 h-1 bg-cyan-400 rounded-full m-auto mt-1 animate-ping"></div>
          </div>
        </div>
        <div className="absolute bottom-1/3 right-1/4 w-2 h-2 bg-gradient-to-r from-emerald-400/30 to-cyan-400/30 rounded-full animate-pulse delay-500" style={{ zIndex: 3 }}></div>
        <div className="absolute top-1/2 left-10 w-4 h-4" style={{ zIndex: 3 }}>
          <div className="w-full h-full bg-emerald-400/20 rounded-full animate-pulse delay-1000">
            <div className="w-1 h-1 bg-emerald-400 rounded-full m-auto mt-1.5 animate-ping delay-1000"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-24 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Trusted by Industry Leaders Worldwide
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Our proven track record speaks for itself with measurable results
              across international markets.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white p-5 md:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4 md:mb-6 mx-auto">
                  <i className={`${stat.icon} text-xl md:text-2xl text-white`}></i>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1 md:mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium text-xs md:text-sm">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-20">
            <div className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-xs md:text-sm font-medium mb-3 md:mb-4">
              Our Expertise
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-8">
              Comprehensive Lead Generation Services
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From advanced SEO strategies to AI-powered solutions, we provide
              end-to-end digital marketing services designed to generate
              high-quality leads for international clients across multiple
              industries.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-white p-5 md:p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border border-gray-100 hover:border-blue-200 transform hover:-translate-y-3"
              >
                <div
                  className={`w-14 h-14 md:w-20 md:h-20 flex items-center justify-center bg-gradient-to-br ${service.color} rounded-3xl mb-5 md:mb-8 group-hover:scale-110 transition-transform`}
                >
                  <i className={`${service.icon} text-2xl md:text-3xl text-white`}></i>
                </div>
                <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4 md:mb-6">
                  {service.description}
                </p>
                <button 
                  onClick={() => {
                    const serviceSlug = service.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                    navigate(`/services/${serviceSlug}`);
                  }}
                  className="text-blue-600 font-semibold hover:text-blue-700 transition-colors flex items-center cursor-pointer text-sm md:text-base"
                >
                  Learn More{' '}
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-12 md:py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-20">
            <div className="inline-block bg-purple-100 text-purple-600 px-4 py-2 rounded-full text-xs md:text-sm font-medium mb-3 md:mb-4">
              Industry Focus
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-8">
              Specialized Expertise Across Key Industries
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We understand the unique challenges and opportunities in
              high-value industries, delivering targeted strategies that convert
              prospects into customers.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="group bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={industry.image}
                    alt={industry.title}
                    className="w-full h-48 md:h-72 object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-xs font-medium">Generated</div>
                        <div className="text-lg md:text-2xl font-bold">{industry.stats.leads}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium">Conversion</div>
                        <div className="text-lg md:text-2xl font-bold">{industry.stats.conversion}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5 md:p-8">
                  <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-blue-600 transition-colors">
                    {industry.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4 md:mb-6">
                    {industry.description}
                  </p>
                  <button 
                    onClick={() => {
                      const serviceSlug = industry.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                      navigate(`/services/${serviceSlug}`);
                    }}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-semibold hover:from-blue-700 hover:to-purple-700 transition-all cursor-pointer"
                  >
                    Explore Solutions
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-20">
            <div className="inline-block bg-green-100 text-green-600 px-4 py-2 rounded-full text-xs md:text-sm font-medium mb-3 md:mb-4">
              Client Success
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-8">
              What Our Clients Say
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what industry leaders say
              about their experience with Halcom Analytics.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white to-gray-50 p-5 md:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="flex items-center mb-4 md:mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-base md:text-xl"></i>
                  ))}
                </div>
                <blockquote className="text-sm md:text-base text-gray-700 mb-4 md:mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover object-top mr-3 md:mr-4"
                  />
                  <div>
                    <div className="font-bold text-gray-900 text-sm md:text-base">{testimonial.name}</div>
                    <div className="text-xs md:text-sm text-gray-600">{testimonial.role}, {testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 relative overflow-hidden">
        <div
          className='absolute inset-0 bg-[url("data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%224%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] opacity-20'
        ></div>
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center relative z-10">
          <div className="mb-5 md:mb-8">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-xs md:text-sm font-medium">
              Ready to Scale?
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl lg:text-6xl font-bold text-white mb-5 md:mb-8 leading-tight">
            Transform Your Business with{' '}
            <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
              Premium Leads
            </span>
          </h2>
          <p className="text-base md:text-xl text-blue-100 mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed">
            Join 180+ international clients who trust Halcom Analytics to deliver
            qualified prospects and drive exponential business growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="text-base md:text-xl px-6 md:px-10 py-3 md:py-5 bg-white text-black hover:bg-gray-100 transform hover:scale-105 transition-all shadow-2xl"
            >
              <i className="ri-phone-line mr-2 md:mr-3 text-black"></i>
              <span className="text-black">Schedule Free Consultation</span>
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/case-studies')}
              className="text-base md:text-xl px-6 md:px-10 py-3 md:py-5 border-2 border-white text-white hover:bg-white hover:text-black transform hover:scale-105 transition-all"
            >
              <i className="ri-file-chart-line mr-2 md:mr-3"></i>
              View Case Studies
            </Button>
          </div>
          <div className="mt-8 md:mt-12 text-blue-200 text-xs md:text-sm">
            🔒 Free consultation • No obligation • Results guaranteed
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
